import { useContext, useEffect } from 'react';

import { Route, Routes, Navigate } from 'react-router-dom';

import { Portal, Box, Flex, Spinner } from '@chakra-ui/react';

import { useQuery } from '@tanstack/react-query';

import TopNavigation from 'layouts/admin/navbar/TopNavigation';
import Sidebar from 'layouts/admin/sidebar/Sidebar';
import Footer from 'layouts/admin/footer/Footer';

import routes from 'routes';
import ComingSoon from 'views/coming_soon';
import {
  ACTURIS_INTEGRATION_FLAG,
  CLIENT_CENTER_LIST_PATH,
} from 'views/client_center';

import { AuthContext } from 'contexts/AuthContext';

import { apiGetFeatureFlags, apiGetBaraUserCapabilities } from 'api';

import { setFeatureFlags } from 'reducers/featureFlagsReducer';
import { setBaraCapabilities } from 'reducers/baraReducer';

import { useAppDispatch, useAppSelector } from 'hooks';
import { useFeatureFlag } from 'hooks/useFeatureFlag';

const AdminLayout = ({ theme, setTheme, ...rest }: { [x: string]: any }) => {
  const { user } = useContext(AuthContext);
  const dispatch = useAppDispatch();
  const isQuoteMarketplaceEnabled = useFeatureFlag('enable-quote-marketplace');
  const isBaraEnabled = useFeatureFlag('enable-bara');
  const isActurisIntegrationEnabled = useFeatureFlag(ACTURIS_INTEGRATION_FLAG);

  const featureFlagsInStore = useAppSelector(
    (state) => state.featureFlags.flags,
  );
  const hasFeatureFlagsInStore = Object.keys(featureFlagsInStore).length > 0;

  const { data: featureFlags } = useQuery({
    queryKey: ['feature-flags'],
    queryFn: apiGetFeatureFlags,
    refetchInterval: 60000,
    refetchOnWindowFocus: true,
  }) as {
    data: FeatureFlag[];
  };

  useEffect(() => {
    if (featureFlags) {
      dispatch(setFeatureFlags(featureFlags));
    }
  }, [featureFlags, dispatch]);

  // Fetch BARA capabilities once on boot — only when FF is enabled
  useEffect(() => {
    if (!isBaraEnabled) return;
    apiGetBaraUserCapabilities()
      .then((data: any) => dispatch(setBaraCapabilities(data)))
      .catch(() => {});
  }, [isBaraEnabled, dispatch]);

  const getRoutes = (routes: RoutesType[]): any => {
    return routes.map((route: RoutesType, key: number) => {
      if (route?.permissions && !route.permissions.includes(user.role)) {
        return null;
      }

      if (
        route.path.startsWith('/quote-marketplace') &&
        !isQuoteMarketplaceEnabled
      ) {
        return <Route path={route.path} element={<ComingSoon />} key={key} />;
      }

      // Client Center is the Acturis-backed replacement for the legacy customer
      // list; with the flag off it is not a section yet. The legacy routes stay
      // reachable either way — only the nav entry swaps.
      if (
        route.path.startsWith(CLIENT_CENTER_LIST_PATH) &&
        !isActurisIntegrationEnabled
      ) {
        return <Route path={route.path} element={<ComingSoon />} key={key} />;
      }

      if (route.layout === '/admin') {
        return (
          <Route path={`${route.path}`} element={route.component} key={key} />
        );
      }

      if (route.collapse) {
        return getRoutes(route.items);
      } else {
        return null;
      }
    });
  };

  return (
    <>
      <Sidebar />

      <Box
        float="right"
        position="relative"
        w={{ base: '100%', hxl: 'calc( 100% - 300px )' }}
      >
        <Portal>
          <TopNavigation theme={theme} setTheme={setTheme} {...rest} />
        </Portal>

        <Box px="20px" pt={{ base: '180px', md: '120px' }} minH="100vh">
          <Routes>
            {getRoutes(routes)}
            <Route path="*" element={<Navigate to="/admin/home" replace />} />
          </Routes>
        </Box>

        <Box>
          <Footer theme={theme} setTheme={setTheme} />
        </Box>

        {!hasFeatureFlagsInStore && (
          <Flex
            position="fixed"
            top="0"
            left="0"
            right="0"
            bottom="0"
            bg="rgba(255, 255, 255, 0.8)"
            backdropFilter="blur(4px)"
            zIndex="9999"
            align="center"
            justify="center"
          >
            <Spinner
              size="xl"
              thickness="4px"
              speed="0.65s"
              color="brand.500"
            />
          </Flex>
        )}
      </Box>
    </>
  );
};

export default AdminLayout;
